import { useState, useEffect } from 'react';
import userService from '../services/userService';

/**
 * Shared hook for technician filtering across pages.
 *
 * Filter values:
 *   'all'          → no filter (admin only)
 *   'f:franchiseeId' → all branches within a franchise
 *   'franchiseeId:branchId' → specific person (franchisee or tech)
 *
 * @param {Object} userProfile - Current user profile
 */
const useTechnicianFilter = (userProfile) => {
  const [techGroups, setTechGroups] = useState([]);
  const [selectedTechnician, setSelectedTechnician] = useState('all');
  const [loaded, setLoaded] = useState(false);

  const isAdmin = userProfile?.role === 'admin';
  const isFranchisee = userProfile?.role === 'franchisee';
  const showFilter = isAdmin || isFranchisee;

  useEffect(() => {
    const fetchTechnicians = async () => {
      if (!userProfile) return;
      try {
        if (isAdmin) {
          const [techResponse, franchiseeResponse] = await Promise.all([
            userService.getUsers({ role: 'technician', limit: 100, active: true }),
            userService.getUsers({ role: 'franchisee', limit: 100, active: true })
          ]);
          const franchisees = franchiseeResponse.users || [];
          const techs = techResponse.users || [];
          const groups = franchisees.map(f => ({
            id: f.id,
            name: f.name,
            branchId: f.branch_id,
            company: f.company_name || '',
            technicians: techs
              .filter(t => t.assigned_franchisee_id === f.id)
              .map(t => ({ id: t.id, name: t.name, branchId: t.branch_id }))
          }));
          setTechGroups(groups);
        } else if (isFranchisee) {
          const response = await userService.getUsers({ limit: 100, active: true });
          const techs = (response.users || [])
            .filter(u => u.id !== userProfile.id)
            .map(u => ({ id: u.id, name: u.name, branchId: u.branch_id }));
          setTechGroups([{
            id: userProfile.id,
            name: userProfile.name,
            branchId: userProfile.branch_id,
            company: userProfile.company_name || '',
            technicians: techs
          }]);
          setSelectedTechnician(`f:${userProfile.id}`);
        }
      } catch (error) {
        console.error('Error fetching technicians:', error);
      } finally {
        setLoaded(true);
      }
    };
    fetchTechnicians();
  }, [userProfile, isAdmin, isFranchisee]);

  /**
   * Get the franchiseeId to scope API calls.
   * 'all' → 'all', franchise/tech selection → franchisee UUID
   */
  const getFilteredFranchiseeId = () => {
    if (selectedTechnician === 'all') {
      // Franchisees should never request 'all' — fall back to own ID
      if (isFranchisee && userProfile?.id) return userProfile.id;
      return 'all';
    }
    if (selectedTechnician.startsWith('f:')) return selectedTechnician.slice(2);
    // Specific person: 'franchiseeId:branchId' → extract franchiseeId
    const colonIdx = selectedTechnician.indexOf(':');
    if (colonIdx > 0) return selectedTechnician.slice(0, colonIdx);
    return 'all';
  };

  /**
   * Get the branch_id for filtering, or null if showing all branches.
   */
  const getSelectedBranchId = () => {
    if (selectedTechnician === 'all' || selectedTechnician.startsWith('f:')) return null;
    const colonIdx = selectedTechnician.indexOf(':');
    if (colonIdx > 0) return selectedTechnician.slice(colonIdx + 1);
    return null;
  };

  return {
    techGroups,
    selectedTechnician,
    setSelectedTechnician,
    showFilter,
    isAdmin,
    isFranchisee,
    loaded,
    getFilteredFranchiseeId,
    getSelectedBranchId
  };
};

export default useTechnicianFilter;
